/**
 * Application route paths.
 *
 * Every navigable URL should be built through this module so that route
 * shapes can be refactored without grep-and-pray. Add new entries here when
 * you introduce a new page.
 */
export const routes = {
  dashboard: () => '/dashboard',
  assessmentList: () => '/assessments',
  assessmentNew: () => '/assessments/new',
  assessmentOverview: (assessmentId: string) =>
    `/assessments/${assessmentId}/overview`,
  assessmentSummary: (assessmentId: string) =>
    `/assessments/${assessmentId}/summary`,
  assessmentDomain: (assessmentId: string, domainId: string | number) =>
    `/assessments/${assessmentId}/domain/${domainId}`,

  // Live progress for a submitted run.
  //
  // No `runs` segment, unlike remediation: NestJS mints a NEW assessment id for
  // every version it evaluates, so an assessment id already identifies exactly
  // one run. There is nothing for a second identifier to disambiguate.
  assessmentProgress: (assessmentId: string) => `/assessments/${assessmentId}/progress`,

  // AI document remediation — a feature in its own right, so the whole flow
  // lives under /remediation rather than hanging off an assessment. A run
  // outlives the assessment version it was started from, and the selection page
  // is reached from the remediation area, not from inside the assessment.
  //
  //   /remediation                      list of completed assessments
  //   /remediation/:assessmentId/scope  document selection
  //   /remediation/runs/:runId          live progress / finished
  //   /remediation/runs/:runId/review   redline review
  //
  // `runs` is a literal segment so it can never be mistaken for an assessment
  // id — react-router matches static segments ahead of params.
  remediationList: () => '/remediation',
  remediationScope: (assessmentId: string) => `/remediation/${assessmentId}/scope`,
  remediationRun: (runId: string) => `/remediation/runs/${runId}`,
  // `itemId` focuses one document in the review; omit it to open the first
  // still-undecided one.
  remediationReview: (runId: string, itemId?: string) =>
    `/remediation/runs/${runId}/review${itemId ? `?item=${itemId}` : ''}`,
  // Between reviewing and finalizing. Review ends with every document decided
  // and nothing showing what those decisions add up to — so the irreversible
  // step (fork a version, re-assess it) was being taken from a confirm dialog.
  // This is where that decision is made instead, with the run's outcome in view
  // and the option to go back and remediate more first.
  remediationSummary: (runId: string) => `/remediation/runs/${runId}/summary`,

  // Between reviewing and the summary. A placeholder exists because the
  // organization profile had no value for it, so this is the one point in the
  // flow where the user is looking at the exact list of facts their profile is
  // missing, with the documents needing them named.
  //
  // The page carries its position in a `?doc=` param of its own, as the review
  // does with `?item=`. No caller links to a specific document yet, so the
  // builder does not take one.
  remediationPlaceholders: (runId: string) => `/remediation/runs/${runId}/placeholders`,

  // Platform super-admin panel (own full-screen shell, gated by platform_admin).
  admin: () => '/admin',
  adminDashboard: () => '/admin/dashboard',
  adminTenants: () => '/admin/organizations',
  adminTenantDetail: (tenantId: string, tab?: string) =>
    `/admin/organizations/${tenantId}${tab ? `?tab=${tab}` : ''}`,
  adminUsers: () => '/admin/users',
  adminDemoRequests: () => '/admin/demo-requests',
  adminFeatures: () => '/admin/features',
  adminUsage: () => '/admin/usage',
} as const;
